import React, { useEffect, useState, useCallback, useRef, useMemo, Suspense } from 'react';
import Sidebar from './components/layout/Sidebar';
import TopBar from './components/layout/TopBar';
import MobileTabBar from './components/layout/MobileTabBar';
import OrderNotificationToast from './components/OrderNotificationToast';
import SaludPanelDrawer from './components/SaludPanelDrawer';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import CatalogPage from './pages/CatalogPage';
import OrdersPage from './pages/OrdersPage';
import SalesResumenPage from './pages/SalesResumenPage';
import { ToastProvider, useToast } from './components/ToastManager';
import BannerSoloLectura from './components/BannerSoloLectura';
import { esSoloLectura, esDesconectada, esSuspendida, avisoFinPrueba, infoSoporte, MENSAJE_SOLO_LECTURA } from './utils/soloLectura';
import SelectorTiendaSoporte from './components/SelectorTiendaSoporte';
import GlobalSearch from './components/GlobalSearch';
import ErrorBoundary from './components/ErrorBoundary';
import { getOrderOrigin, getPaymentLabel } from './utils/orderUtils';
import { esCelularColombiano } from './utils/celularColombiano'; // pos-boton-wa: ¿el pedido del polling trae celular?
import { aplicarLote, fusionarPorId } from './utils/mergeProducts';
import { productsListUrl } from './utils/productApi';
import { cargarConfigConReintentos } from './utils/cargarConfig';
import { limpiarDatosDeTienda } from './utils/limpiarDatosTienda'; // F6: cambio de usuario no arrastra datos de tienda
import './App.css';

const SalesPage     = React.lazy(() => import('./pages/SalesPage'));
const InventoryPage = React.lazy(() => import('./pages/InventoryPage'));
const CustomersPage = React.lazy(() => import('./pages/CustomersPage'));
const SettingsPage  = React.lazy(() => import('./pages/SettingsPage'));
const UsersPage     = React.lazy(() => import('./pages/UsersPage'));
const UiKitPage     = React.lazy(() => import('./pages/UiKitPage'));
const MaquetaWebPage = React.lazy(() => import('./pages/MaquetaWebPage')); // WEB-R R-A (maqueta dev-only)
const CashFlowPage  = React.lazy(() => import('./pages/CashFlowPage'));
const FinanzasPage  = React.lazy(() => import('./pages/FinanzasPage'));
const CostsPage     = React.lazy(() => import('./pages/CostsPage'));
const AnalyticsPage = React.lazy(() => import('./pages/AnalyticsPage')); // F1-B1: flag por tienda + solo admin
const AgendaPage    = React.lazy(() => import('./agenda/AgendaPage'));   // AG-1: flag por tienda + admin/coordinador
// Lazy A PROPÓSITO (perf, 21-sep-2026): AssistantChat arrastra recharts (~150 kB
// gzip) al bundle inicial si se importa estático — y el Hunter puede no abrirse
// nunca en una sesión. Se monta recién en la PRIMERA apertura (ver assistantMounted).
const AssistantChat = React.lazy(() => import('./components/AssistantChat'));

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/* S2d — antes esto se llamaba `isActiveHours()` y gobernaba DOS cosas distintas:
   si el canal SSE estaba abierto y si se mandaba push al celular. Se separaron.

   El canal SSE ya no tiene horario. La regla original (4c2cef2, 26-jun-2026) no
   era arbitraria: estábamos en el tier gratuito de Render y existía el riesgo
   real de que lo quitaran, así que una conexión permanente era plata que no
   había. Ese motivo desapareció — plan Starter, sin spin-down. Y el argumento
   que quedaba en pie, que el refresco vaciaba la pantalla, lo mató S2b: ahora el
   refresco funde en vez de reemplazar, así que el canal solo suma información.

   El push al celular SÍ conserva el horario, y por eso esta función sigue
   existiendo con un nombre que dice para qué sirve: un push a las 3 de la mañana
   por un pedido web no lo quiere nadie.

   Colombia es UTC-5, sin horario de verano (DST). */
function esHorarioDeAvisos() {
  const colombiaHour = (new Date().getUTCHours() - 5 + 24) % 24;
  return colombiaHour >= 9 && colombiaHour < 22; // 9:00am – 10:00pm
}

/* P1d (PUL-OCT, Miguel 13-oct-2026): el iOS Large Title desplegable SE RETIRÓ para todas las
   páginas — el patrón único es el de Ventas: el título vive FIJO debajo de la barra (en el
   PageHeader/hero propio de cada página) y la barra queda solo con buscador e iconos. El set
   LARGE_TITLE_PAGES, el IntersectionObserver de titleCollapsed, el hero ios-large-title y el
   título colapsado del TopBar se eliminaron con él (los subtítulos ya vivían duplicados en la
   description del PageHeader de cada página). */

const PTR_THRESHOLD = 65;

// Páginas permitidas por rol
// Caja y Costos: solo admin/dueña (dueña = admin con es_owner=true).
const ROLE_ALLOWED = {
  admin:       new Set(['home', 'catalog', 'inventory', 'sales', 'orders', 'resumen', 'customers', 'cashflow', 'finanzas', 'costs', 'analitica', 'agenda', 'settings']),
  coordinador: new Set(['home', 'catalog', 'inventory', 'sales', 'orders', 'resumen', 'customers', 'cashflow', 'costs', 'agenda']),
  // inicio-centro-control B2: el vendedor GANA 'home' — el Centro de control le muestra sus
  // 4 cuadros (ventas de hoy, por despachar, gestionados hoy, por confirmar; permisos de Miguel).
  vendedor:    new Set(['home', 'sales', 'orders', 'resumen', 'inventory', 'catalog']),
};

// Las gemelas de la auditoría (2026-07-21): Marketing y Tendencias dejaron de
// ser destinos del sidebar y viven como tabs en Clientes y Resumen. El nombre
// viejo nunca cae al vacío — cualquier caller legacy redirige al tab nuevo.
const LEGACY_PAGE_MAP = {
  marketing: { page: 'customers', tab: 'marketing' },
  trends:    { page: 'resumen',   tab: 'trends' },
  // 'audit' nunca fue una página: es el tab Auditoría de Usuarios. El botón del
  // popover "···" navegaba a un id sin render (F1 lo cazó) — mismo mecanismo.
  audit:     { page: 'users',     tab: 'audit' },
};

// Galería del sistema de diseño (F2) — SOLO DEV: CRA elimina la rama en el
// build de producción (NODE_ENV es literal en compile time).
const IS_DEV = process.env.NODE_ENV === 'development';

function canAccess(page, role, isOwner = false) {
  if (page === 'uikit') return IS_DEV && (role === 'admin' || isOwner);
  if (page === 'maquetaweb') return IS_DEV && (role === 'admin' || isOwner);
  if (page === 'users') return isOwner;        // solo la dueña
  if (page === 'settings') return role === 'admin' || isOwner;
  if (page === 'analitica') return role === 'admin' || isOwner; // F1-B1: además exige el flag (se chequea en el render)
  if (isOwner) return true;                    // owner tiene acceso total
  const allowed = ROLE_ALLOWED[role] ?? ROLE_ALLOWED.vendedor;
  return allowed.has(page);
}

function getDefaultPage(role) {
  // Siempre abrir Resumen primero; Ventas se precarga en background
  if (ROLE_ALLOWED[role]?.has('resumen') ?? false) return 'resumen';
  if (ROLE_ALLOWED[role]?.has('sales')   ?? false) return 'sales';
  return 'home';
}

// Detectar si corre dentro del ejecutable Electron (userAgent contiene "Electron")
const IS_ELECTRON = navigator.userAgent.toLowerCase().includes('electron');

// Parchar fetch globalmente para: (1) inyectar Authorization: Bearer token,
// (2) emitir evento cuando el backend devuelva 401 (sesión expirada)
if (!IS_ELECTRON) {
  const _origFetch = window.fetch.bind(window);
  window.fetch = async (url, opts = {}) => {
    if (typeof url === 'string' && url.startsWith(API_URL)) {
      const token = localStorage.getItem('token');
      if (token) {
        opts = {
          ...opts,
          headers: { ...opts.headers, Authorization: `Bearer ${token}` },
        };
      }
    }
    const res = await _origFetch(url, opts);
    if (res.status === 401 && typeof url === 'string' && url.startsWith(API_URL)) {
      localStorage.removeItem('token');
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
    // F7: tienda de solo lectura (Shopify v1). Cualquier escritura la corta el backend con 409
    // `solo_lectura`; acá lo convertimos en un aviso claro (nunca un fallo mudo), sin consumir
    // el cuerpo que el llamador espera (res.clone()).
    if (res.status === 409 && typeof url === 'string' && url.startsWith(API_URL)) {
      res.clone().json().then((b) => {
        if (b && b.error === 'solo_lectura') {
          window.dispatchEvent(new CustomEvent('tienda:solo-lectura', { detail: b.mensaje || '' }));
        }
      }).catch(() => {});
    }
    return res;
  };
}

// Captura global de errores no manejados — para diagnóstico de pantalla en blanco
window.onerror = (msg, src, line, col, error) => {
  console.error('[global] Error no capturado:', msg, error);
};
window.onunhandledrejection = (event) => {
  console.error('[global] Promesa rechazada sin catch:', event.reason);
};

// F7: escucha el 409 `solo_lectura` (dispara el fetch parcheado) y muestra un aviso claro.
// Vive DENTRO de <ToastProvider> para poder usar useToast.
function AvisoSoloLecturaGlobal() {
  const { showToast } = useToast();
  useEffect(() => {
    const h = (e) => showToast({ message: (e.detail && String(e.detail)) || MENSAJE_SOLO_LECTURA, variant: 'warning' });
    window.addEventListener('tienda:solo-lectura', h);
    return () => window.removeEventListener('tienda:solo-lectura', h);
  }, [showToast]);
  return null;
}

function App() {
  // ── Autenticación ───────────────────────────────────────────────────────────
  // Electron siempre está autenticado; en web verificamos la cookie con el backend
  const [isAuthenticated, setIsAuthenticated] = useState(IS_ELECTRON);
  const [authChecking,    setAuthChecking]    = useState(!IS_ELECTRON);

  useEffect(() => {
    if (IS_ELECTRON) return;

    // Despertar el servidor en background (Render free tier puede estar dormido)
    fetch(`${API_URL}/api/warmup`).catch(() => {});

    const token = localStorage.getItem('token');
    if (!token) {
      setIsAuthenticated(false);
      setAuthChecking(false);
      return;
    }

    // Decodificar JWT localmente — sin llamada al servidor
    let payload;
    try {
      const b64 = token.split('.')[1] || '';
      payload = JSON.parse(atob(b64.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      localStorage.removeItem('token');
      setIsAuthenticated(false);
      setAuthChecking(false);
      return;
    }

    const expMs   = (payload.exp || 0) * 1000;
    const hasRole = !!payload.role;

    if (!hasRole || expMs < Date.now()) {
      // Token expirado o sin rol → login inmediato
      localStorage.removeItem('token');
      setIsAuthenticated(false);
      setAuthChecking(false);
      return;
    }

    // Token localmente válido → mostrar dashboard YA (sin esperar al servidor)
    setIsAuthenticated(true);
    setAuthChecking(false);

    // Verificación en background: detectar cuentas desactivadas, tokens revocados, etc.
    // Si el servidor responde 401 → redirigir al login.
    // Si el servidor tarda más de 3s o falla → mantener sesión activa.
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), 3_000);
    fetch(`${API_URL}/api/auth/check`, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) {
          localStorage.removeItem('token');
          setIsAuthenticated(false);
        }
      })
      .catch(() => { /* timeout o red offline — el token local es válido */ })
      .finally(() => clearTimeout(tid));
    return () => { controller.abort(); clearTimeout(tid); };
  }, []);

  // Sesión expirada en cualquier llamada posterior → volver al login
  useEffect(() => {
    if (IS_ELECTRON) return;
    const handleUnauth = () => setIsAuthenticated(false);
    window.addEventListener('auth:unauthorized', handleUnauth);
    return () => window.removeEventListener('auth:unauthorized', handleUnauth);
  }, []);

  const handleLogout = useCallback(() => {
    if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
    // F6-aislamiento (frontend): borra los datos de tienda PERSISTENTES y RECARGA — la recarga vacía
    // todo el estado en memoria (productos, config, notificaciones) para que el próximo usuario no vea
    // nada del anterior (bug del catálogo con productos de otra tienda hasta Ctrl+F5).
    limpiarDatosDeTienda();
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    if (!IS_ELECTRON) window.location.reload();
  }, []);

  // ── Push Notifications ────────────────────────────────────────────────────
  const setupPushNotifications = useCallback(async () => {
    if (IS_ELECTRON) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (localStorage.getItem('push_permission') === 'denied') return;

    try {
      const reg = await navigator.serviceWorker.register('/sw.js');

      // Escuchar mensajes del SW para navegar a Pedidos al hacer clic en notif
      navigator.serviceWorker.addEventListener('message', (evt) => {
        if (evt.data?.type === 'NAVIGATE' && evt.data?.page) {
          setActivePage(evt.data.page);
        }
      });

      // Si ya hay suscripción activa, no volver a pedir permiso
      const existing = await reg.pushManager.getSubscription();
      if (existing) return;

      // Pedir permiso (solo si no dijo "denied" antes)
      const permission = await Notification.requestPermission();
      localStorage.setItem('push_permission', permission);
      if (permission !== 'granted') return;

      // Obtener clave pública VAPID del backend
      const keyRes = await fetch(`${API_URL}/api/push/vapid-public-key`);
      if (!keyRes.ok) return;
      const { publicKey } = await keyRes.json();

      // Convertir base64url a Uint8Array
      const rawKey = publicKey.replace(/-/g, '+').replace(/_/g, '/');
      const padding = '='.repeat((4 - (rawKey.length % 4)) % 4);
      const binaryStr = atob(rawKey + padding);
      const applicationServerKey = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) applicationServerKey[i] = binaryStr.charCodeAt(i);

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription }),
      });
    } catch (err) {
      console.warn('[push] setup error:', err.message);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAuthenticated) setupPushNotifications();
  }, [isAuthenticated, setupPushNotifications]);

  const { userName, userRole, isOwner, esSuperadmin } = useMemo(() => {
    if (IS_ELECTRON) return { userName: 'Admin', userRole: 'admin', isOwner: true, esSuperadmin: false };
    if (!isAuthenticated) return { userName: 'Usuario', userRole: 'admin', isOwner: false, esSuperadmin: false };
    try {
      const token = localStorage.getItem('token');
      const b64   = (token || '').split('.')[1] || '';
      const pay   = JSON.parse(atob(b64.replace(/-/g, '+').replace(/_/g, '/')));
      const raw = (pay.role || 'admin').toLowerCase();
      // F9: el superadmin de plataforma se trata como ADMIN para el acceso del panel (el backend
      // ya lo deja pasar por el comodín de requireRole); la condición de superadmin se guarda
      // aparte para el selector de tienda y la franja de soporte.
      const esSuperadmin = raw === 'superadmin';
      return { userName: pay.username || 'Usuario', userRole: esSuperadmin ? 'admin' : raw, isOwner: !!pay.es_owner, esSuperadmin };
    } catch {
      return { userName: 'Usuario', userRole: 'admin', isOwner: false, esSuperadmin: false };
    }
  }, [isAuthenticated]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // B4: la pestaña de Analítica vive en la URL (#analitica/<tab>) para que la
  // recarga no la pierda — si el hash lo dice, se arranca en Analítica (el
  // guard de rol de abajo y el gate del flag devuelven al default si no aplica).
  const [activePage, setActivePage] = useState(() => (window.location.hash.startsWith('#analitica') ? 'analitica' : 'resumen'));

  // B4: el hash #analitica/<tab> es de la página Analítica — al salir de ella
  // se limpia (una URL que promete Analítica desde otra página mentiría en la
  // próxima recarga).
  useEffect(() => {
    if (activePage !== 'analitica' && window.location.hash.startsWith('#analitica')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [activePage]);

  // ── iOS Virtual Keyboard detection ────────────────────────────────────────
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  useEffect(() => {
    if (window.visualViewport) {
      // visualViewport.height drops when the keyboard opens on iOS Safari
      const handler = () => {
        setKeyboardVisible(window.visualViewport.height < window.innerHeight * 0.75);
      };
      window.visualViewport.addEventListener('resize', handler);
      return () => window.visualViewport.removeEventListener('resize', handler);
    }
    // Fallback for browsers without visualViewport support
    const INPUTS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
    const onIn = (e) => {
      if (INPUTS.has(e.target.tagName) || e.target.isContentEditable) setKeyboardVisible(true);
    };
    const onOut = () => {
      setTimeout(() => {
        const a = document.activeElement;
        if (!a || (!INPUTS.has(a.tagName) && !a.isContentEditable)) setKeyboardVisible(false);
      }, 150);
    };
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
    };
  }, []);

  // ── Pull-to-Refresh (móvil) ────────────────────────────────────────────────
  const pageContentRef  = useRef(null);
  const ptrDistRef      = useRef(0);
  const ptrRefreshRef   = useRef(false);
  const activePageRef   = useRef(activePage);
  const [ptrDist,        setPtrDist]        = useState(0);
  const [ptrRefreshing,  setPtrRefreshing]  = useState(false);

  useEffect(() => { activePageRef.current = activePage; }, [activePage]);

  // ── Prefetch post-login ─────────────────────────────────────────────────────
  const [prefetchActive, setPrefetchActive] = useState(false);
  const prefetchStartedRef = useRef(false);
  // W2: frescura de la copia local (viaja en cada respuesta de /api/products).
  const [espejoInfo, setEspejoInfo] = useState(null);

  // Redirigir al módulo principal si el rol no tiene acceso a la página actual
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!canAccess(activePage, userRole, isOwner)) {
      if (activePage === 'cashflow') {
        window.alert('No tienes permiso para acceder al módulo Caja.');
      } else if (activePage === 'costs') {
        window.alert('No tienes permiso para acceder al módulo Costos.');
      } else if (activePage === 'analitica') {
        // inicio-centro-control B1: Analítica es solo para administradores. Quien entra por
        // la URL directa (#analitica) ve este mensaje y vuelve a su página — ni datos ni
        // pantalla en blanco. El backend igual rechaza (/api/analitica/* es admin-only):
        // esto es presentación, no la defensa.
        window.alert('El módulo Analítica es solo para administradores.');
      }
      setActivePage(getDefaultPage(userRole));
    }
  }, [activePage, userRole, isOwner, isAuthenticated]);

  // ── App config (loaded once, shared across all pages) ───────────────────────
  // H1 (23-sep-2026): con REINTENTOS y sin fabricar {}. En el ejecutable la
  // ventana abre antes de que el backend escuche: el fetch único moría y un
  // config={} pintaba todos los flags como apagados mientras el servidor los
  // tenía guardados encendidos. null = «todavía no cargó», nunca «apagado».
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let abortado = false;
    cargarConfigConReintentos(() => fetch(`${API_URL}/api/config`), { cancelado: () => abortado })
      .then((cfg) => { if (!abortado && cfg) setConfig(cfg); });
    return () => { abortado = true; };
  }, [isAuthenticated]);

  const handleConfigUpdate = useCallback((updates) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  // B4: si se llegó a Analítica por el hash pero el flag por tienda está
  // APAGADO (config ya cargada), no hay página que mostrar — al default.
  useEffect(() => {
    if (activePage === 'analitica' && config && !config.analitica_activa) setActivePage(getDefaultPage(userRole));
    // AG-1: mismo criterio para Agenda — sin el flag por tienda no hay página.
    if (activePage === 'agenda' && config && !config.agenda_activa) setActivePage(getDefaultPage(userRole));
  }, [activePage, config, userRole]);

  // Global search
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  // Hunter: estado del panel elevado a App (F4 §14). El disparador vive en el
  // sidebar (desktop) y en la topbar (móvil); murió el FAB flotante.
  const [assistantOpen, setAssistantOpen] = useState(false);
  // El chat se monta en la PRIMERA apertura y queda montado (conserva la
  // conversación al cerrar/reabrir, como siempre). Antes del primer open, ni el
  // componente ni su chunk (recharts incluido) se cargan; sus efectos ya estaban
  // gateados por `open`, así que cerrado no hacía nada que se pierda al diferir.
  const [assistantMounted, setAssistantMounted] = useState(false);
  useEffect(() => { if (assistantOpen) setAssistantMounted(true); }, [assistantOpen]);

  // Panel de salud (admin): lector del endpoint que persiste el sync. El badge sale de
  // total_acciones; el ícono está siempre para que el estado vacío sea alcanzable.
  const [saludOpen, setSaludOpen] = useState(false);
  const [saludData, setSaludData] = useState(null);
  const puedeVerSalud = userRole === 'admin' || isOwner;
  const refetchSalud = useCallback(() => {
    if (!puedeVerSalud) return;
    fetch(`${API_URL}/api/salud/panel`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setSaludData(d); })
      .catch(() => {});
  }, [puedeVerSalud]);
  useEffect(() => { refetchSalud(); }, [refetchSalud]);

  // Catalog state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const productsFetchIdRef = useRef(0);

  // Orders polling + SSE state
  const highestOrderIdRef = useRef(0);
  const sseRef            = useRef(null); // EventSource activo (S2d: sin horario)
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [orderToast, setOrderToast]       = useState(null); // array de notif objects | null

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  function playBeep() {
    try {
      const ctx  = new (window.AudioContext || window.webkitAudioContext)();
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.6);
    } catch (_) { /* audio not available */ }
  }

  // ── Products fetch (carga progresiva) ───────────────────────────────────────
  // Estrategia: pedir page=1 con per_page=24 → mostrar inmediato. Luego cargar
  // las páginas restantes en background y appendear, sin bloquear el render.
  // `silent` = refresco disparado por una venta ajena que llegó por SSE, no por
  // una navegación. En ese caso NO se reemplaza la lista ni se enciende
  // `loading`: quien esté en Catálogo o en Inicio no pidió nada y no tiene por
  // qué ver la lista vaciarse (medido antes de S2b#4: Catálogo 30 → 10 filas,
  // CLS +0.8623) ni los KPIs volver al guion.
  const fetchProducts = useCallback(async ({ nocache = false, silent = false } = {}) => {
    const fetchId = ++productsFetchIdRef.current; // invalida fetches anteriores
    if (!silent) { setLoading(true); setError(null); }
    setLoadingMore(false);

    // Tanda 3b (defecto 1b): la copia local incluye draft/private/pending
    // (status=any vía productsListUrl); Catálogo los muestra con su etiqueta y
    // los KPIs de Inicio filtran a publicados.
    // W2: /api/products lee del ESPEJO — esta carga ya no le cuesta llamadas a
    // Woo (antes: ~120-150 por apertura fría; ahora: 0).
    const buildUrl = (page) => productsListUrl({ page, nocache });

    try {
      // Lote 1 — UI se desbloquea aquí
      const res = await fetch(buildUrl(1));
      if (!res.ok) throw new Error('Error al obtener productos');
      const data = await res.json();
      if (fetchId !== productsFetchIdRef.current) return;
      setProducts((prev) => aplicarLote(prev, data.products || [], silent));
      setTotal(data.total || 0);
      // W2: la respuesta declara la frescura de la copia — se guarda para la franja.
      setEspejoInfo({ hace: data.espejo_actualizado_hace_seg ?? null, advertencia: data.espejo_advertencia || null, vacio: data.espejo_vacio === true, migrando: data.espejo_migrando === true, saludSync: data.salud_sync || null });
      if (!silent) setLoading(false);

      const totalPages = data.total_pages || 1;
      if (totalPages <= 1) return;

      // Lotes 2..N en background — secuencial para no saturar al backend
      setLoadingMore(true);
      for (let page = 2; page <= totalPages; page++) {
        if (fetchId !== productsFetchIdRef.current) return;
        try {
          const r = await fetch(buildUrl(page));
          if (!r.ok) continue;
          const d = await r.json();
          if (fetchId !== productsFetchIdRef.current) return;
          // En modo silencioso los lotes 2..N también funden: si concatenaran,
          // duplicarían todo lo que el lote 1 acaba de fundir.
          setProducts((prev) => (silent
            ? fusionarPorId(prev, d.products || [])
            : prev.concat(d.products || [])));
        } catch (_) { /* continuar con el siguiente lote */ }
      }
    } catch (err) {
      if (fetchId === productsFetchIdRef.current && !silent) {
        setError(err.message);
        setLoading(false);
      }
    } finally {
      if (fetchId === productsFetchIdRef.current) setLoadingMore(false);
    }
  }, []);

  // ── refreshPage para PTR: usar fetchProducts ya definido ────────────────────
  const refreshPage = useCallback(async () => {
    const page = activePageRef.current;
    if (['sales', 'catalog', 'inventory', 'home'].includes(page)) {
      await fetchProducts({ nocache: true });
    } else {
      window.dispatchEvent(new CustomEvent('mobile:refresh', { detail: { page } }));
      await new Promise((r) => setTimeout(r, 700));
    }
  }, [fetchProducts]);

  // Touch listeners para Pull-to-Refresh
  useEffect(() => {
    const el = pageContentRef.current;
    if (!el) return;
    const state = { startY: 0, pulling: false };

    const onStart = (e) => {
      if (el.scrollTop > 2 || ptrRefreshRef.current) return;
      state.startY  = e.touches[0].clientY;
      state.pulling = true;
      ptrDistRef.current = 0;
    };
    const onMove = (e) => {
      if (!state.pulling) return;
      const diff = e.touches[0].clientY - state.startY;
      if (diff <= 0) { state.pulling = false; ptrDistRef.current = 0; setPtrDist(0); return; }
      try { e.preventDefault(); } catch (_) {}
      const clamped = Math.min(diff * 0.42, 80);
      ptrDistRef.current = clamped;
      setPtrDist(clamped);
    };
    const onEnd = async () => {
      if (!state.pulling) return;
      state.pulling = false;
      const dist = ptrDistRef.current;
      ptrDistRef.current = 0;
      setPtrDist(0);
      if (dist >= PTR_THRESHOLD && !ptrRefreshRef.current) {
        ptrRefreshRef.current = true;
        setPtrRefreshing(true);
        await refreshPage();
        setPtrRefreshing(false);
        ptrRefreshRef.current = false;
      }
      state.startY = 0;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove',  onMove,  { passive: false });
    el.addEventListener('touchend',   onEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove',  onMove);
      el.removeEventListener('touchend',   onEnd);
      state.pulling = false;
      setPtrDist(0);
    };
  }, [refreshPage]);

  // W2: esto YA NO es una precarga a Woo — es la carga inicial del estado de
  // productos DESDE EL ESPEJO (0 llamadas a la tienda, respuesta inmediata).
  // Conserva el diferido (onReady del Resumen + fallback) solo para no competir
  // por el hilo de render durante la primera pintada.
  const startPrefetch = useCallback(async () => {
    if (prefetchStartedRef.current) return;
    prefetchStartedRef.current = true;
    await new Promise((r) => setTimeout(r, 3000));
    setPrefetchActive(true);
    try {
      await fetchProducts();
    } finally {
      setPrefetchActive(false);
    }
  }, [fetchProducts]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const tid = setTimeout(startPrefetch, 15_000);
    return () => clearTimeout(tid);
  }, [isAuthenticated, startPrefetch]);

  const handleRefreshProducts = useCallback(() => {
    fetchProducts({ nocache: true });
  }, [fetchProducts]);

  // W2: «Actualizar» — relee de la TIENDA solo los ids visibles (interactivo),
  // actualiza el espejo y refresca la lista; devuelve el resumen para la franja.
  const releerVisiblesEspejo = useCallback(async (ids) => {
    const r = await fetch(`${API_URL}/api/espejo/releer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      body: JSON.stringify({ ids }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || `el servidor respondió ${r.status}`);
    await fetchProducts({ silent: true });
    return d;
  }, [fetchProducts]);

  // W2: espejo vacío → disparar la sincronización inicial (reconciliador en fondo).
  const migrarEspejo = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/espejo/migracion`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'No se pudo arrancar la sincronización'); }
    return r.json();
  }, []);

  // ── Ctrl+K / Cmd+K → abrir búsqueda global ─────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setGlobalSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isAuthenticated]);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const [navTab, setNavTab] = useState(null);
  /* Reset del módulo activo (Miguel 2026-07-23, patrón estándar): clicar el
     ícono del módulo en el que ya estás = VOLVER A SU INICIO (remonta la
     página: lista base, sin inspector ni edición, filtros por defecto, scroll
     arriba). Genérico para todos los módulos vía la key del ErrorBoundary. */
  const [pageResetKey, setPageResetKey] = useState(0);
  // (usa el activePageRef ya existente del PTR, declarado arriba)

  const handleNavigate = useCallback((page) => {
    const legacy = LEGACY_PAGE_MAP[page];
    if (legacy) {
      setNavTab({ tab: legacy.tab, ts: Date.now() });
      page = legacy.page;
    } else {
      // La navegación simple (tab bar, menú) MATA la intención anterior: un navTab
      // viejo re-aplicado al remontar era tolerable para una pestaña, pero con
      // intenciones que abren modales (C1 P5 P4: Caja→retiro) sería un modal
      // fantasma en cada visita. Mismo espíritu que el reset del módulo activo.
      // navigateTo pone su intención DESPUÉS de llamarme, así que no la pisa.
      setNavTab(null);
    }
    if (activePageRef.current === page) setPageResetKey((k) => k + 1);
    setActivePage(page);
    setMobileMenuOpen(false);
    // Scroll al tope al cambiar de módulo (P1d: el large title y su estado se retiraron)
    if (pageContentRef.current) pageContentRef.current.scrollTop = 0;
    if (page === 'orders') {
      setNewOrdersCount(0);
      setOrderToast(null);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  }, []);

  // Navegación con pestaña/sección de destino (usada por Hunter) y, desde F3,
  // con REGISTRO de destino (deep-link del buscador global: la página destino
  // selecciona el registro y abre su inspector). Cambia de módulo y deja una
  // intención que la página de destino lee al montarse.
  const navigateTo = useCallback((page, tab, recordId = null, variationId = null) => {
    handleNavigate(page);
    // Si el nombre era legacy, handleNavigate ya fijó el tab del destino nuevo.
    if (!LEGACY_PAGE_MAP[page]) {
      // variationId viaja junto al recordId (deep-link del panel de salud → Declarar costo con
      // producto Y variación); las páginas que sólo leen tab/recordId lo ignoran.
      setNavTab((tab || recordId) ? { tab: tab || null, recordId, variationId, ts: Date.now() } : null);
    }
  }, [handleNavigate]);

  // ── Orders polling ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    // Init: capture current highest order ID as baseline
    const init = async () => {
      try {
        const res = await fetch(`${API_URL}/api/orders?per_page=1`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.orders?.length > 0) {
          highestOrderIdRef.current = data.orders[0].id;
        }
      } catch (_) { /* ignore */ }
    };
    init();
  }, [isAuthenticated]);

  /* S2d — acá vivía el gestor de horario del SSE: un setInterval de 15 min que
     encendía y apagaba el canal. Se fue con el horario. De paso se lleva su
     defecto: el intervalo estaba anclado al arranque de la sesión, así que el
     corte de las 22:00 llegaba hasta 15 minutos tarde y en un momento distinto
     en cada dispositivo. */

  // ── Polling de respaldo: cada 5 min, a cualquier hora (S2d) ─────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    let timer;
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/orders?per_page=5`);
        if (!res.ok) return;
        const data = await res.json();
        const orders = data.orders || [];
        if (orders.length === 0) return;

        const currentHighest = highestOrderIdRef.current;
        if (currentHighest === 0) {
          highestOrderIdRef.current = orders[0].id;
          return;
        }

        const newOnes = orders.filter((o) => o.id > currentHighest);
        if (newOnes.length > 0) {
          highestOrderIdRef.current = orders[0].id;
          setNewOrdersCount((c) => c + newOnes.length);
          const now = new Date();
          const notifObjects = newOnes.map((o) => ({
            id:          `${o.id}-${now.getTime()}`,
            orderId:     o.id,
            orderNumber: o.number,
            rawTotal:    o.total,
            payment:     getPaymentLabel(o).label,
            origin:      getOrderOrigin(o).label,
            items:       (o.line_items || []).map(i => ({ name: i.name, quantity: i.quantity })),
            waDisponible: esCelularColombiano(o.billing?.phone || ''), // pos-boton-wa
            time:        now,
            read:        false,
          }));
          setNotifications((prev) => [...notifObjects, ...prev].slice(0, 20));
          setOrderToast(notifObjects);
          playBeep();
          // Notificar módulos activos que hay pedidos nuevos
          window.dispatchEvent(new CustomEvent('app:data-changed', { detail: { types: ['orders'] } }));

          // Push para pedidos web (no POS) — solo en horario activo. Este SÍ
          // conserva el horario (S2d): despierta un celular.
          if (esHorarioDeAvisos()) {
            const webOrders = newOnes.filter((o) => {
              const metaMap = {};
              for (const m of (o.meta_data || [])) metaMap[m.key] = m.value;
              return metaMap['origen'] !== 'POS';
            });
            if (webOrders.length > 0) {
              fetch(`${API_URL}/api/push/notify-web-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orders: webOrders }),
              }).catch(() => {});
            }
          }
        }
      } catch (_) { /* ignore polling errors */ }
    };

    // S2d — antes el intervalo se estiraba a 10 min fuera de horario. Queda fijo
    // en 5: es el respaldo de un canal que ahora está abierto siempre, y su costo
    // (una petición de 5 pedidos) no justifica dos regímenes.
    const DELAY = 5 * 60_000;
    const scheduleNext = async () => {
      await poll();
      timer = setTimeout(scheduleNext, DELAY);
    };
    timer = setTimeout(scheduleNext, DELAY);
    return () => clearTimeout(timer);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SSE: tiempo real a cualquier hora (S2d — antes solo 9am–10pm Colombia) ──
  useEffect(() => {
    if (!isAuthenticated || IS_ELECTRON) {
      // Al cerrar sesión, cerrar la conexión si estaba abierta
      if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;

    const es = new EventSource(`${API_URL}/api/events?token=${encodeURIComponent(token)}`);
    sseRef.current = es;

    es.addEventListener('nueva-venta', (e) => {
      try {
        const order = JSON.parse(e.data);
        if (order.id <= highestOrderIdRef.current) return;
        highestOrderIdRef.current = order.id;
        const now = new Date();
        const notif = {
          id:          `${order.id}-${now.getTime()}`,
          orderId:     order.id,
          orderNumber: order.number,
          rawTotal:    order.total,
          payment:     order.canal || 'POS',
          origin:      'POS',
          items:       order.items || [],
          waDisponible: !!order.wa_disponible, // pos-boton-wa: lo decide el backend en el SSE
          time:        now,
          read:        false,
        };
        setNewOrdersCount((c) => c + 1);
        setNotifications((prev) => [notif, ...prev].slice(0, 20));
        setOrderToast([notif]);
        playBeep();
        /* S2c#7 — este evento anuncia UN PEDIDO NUEVO, no un cambio de catálogo:
           por eso va solo 'orders'. El backend emite 'nueva-venta' y
           'data-changed' juntos (server.js:5664 y :5671), y es el segundo el que
           trae 'products' y además dispara el fetch. Decir 'products' acá hacía
           que Inventario se recargara DOS VECES por la misma venta (medido: 6
           peticiones donde se esperaban 3). No se resuelve confiando en que
           nadie separe los dos broadcast del backend: se recorta el evento para
           que diga lo que realmente significa. */
        window.dispatchEvent(new CustomEvent('app:data-changed', { detail: { types: ['orders'] } }));
      } catch (_) { /* parse error */ }
    });

    es.addEventListener('data-changed', (e) => {
      try {
        const { types } = JSON.parse(e.data);
        if (!Array.isArray(types)) return;
        if (types.includes('products')) fetchProducts({ nocache: true, silent: true });
        window.dispatchEvent(new CustomEvent('app:data-changed', { detail: { types } }));
      } catch (_) {}
    });

    es.addEventListener('count-session-update', (e) => {
      try {
        const payload = JSON.parse(e.data);
        window.dispatchEvent(new CustomEvent('app:count-session-update', { detail: payload }));
      } catch (_) {}
    });

    es.onerror = () => { /* EventSource reconecta automáticamente */ };

    return () => {
      es.close();
      if (sseRef.current === es) sseRef.current = null;
    };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  if (authChecking) return <div className="status-msg">Verificando sesión…</div>;
  if (!isAuthenticated) return (
    <LoginPage onLogin={(role) => {
      // F6-aislamiento (frontend): limpia los datos de tienda del usuario anterior y RECARGA, para
      // arrancar en limpio como el nuevo usuario. Cubre el caso de un 401/expiración que dejó
      // isAuthenticated=false SIN recargar y luego entra OTRO usuario en la misma pestaña.
      limpiarDatosDeTienda();
      setIsAuthenticated(true);
      setActivePage(getDefaultPage(role));
      if (!IS_ELECTRON) window.location.reload();
    }} />
  );

  return (
    <ToastProvider>
    <AvisoSoloLecturaGlobal />
    <div className="app-shell">
      <Sidebar
        activePage={activePage}
        onNavigate={handleNavigate}
        badges={{ orders: newOrdersCount }}
        config={config}
        onLogout={handleLogout}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
        onOpenAssistant={() => setAssistantOpen(true)}
        assistantName={config?.tienda?.asistente || config?.asistente?.nombre}
        userRole={userRole}
        isOwner={isOwner}
      />
      <div className={`app-body${keyboardVisible ? ' app-body--kb' : ''}`}>
        <div className="app-top-bar">
          <TopBar
            onOpenSearch={() => setGlobalSearchOpen(true)}
            userName={userName}
            userRole={userRole}
            onLogout={handleLogout}
            onMobileMenu={() => setMobileMenuOpen(v => !v)}
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAllRead={markAllRead}
            onNavigateOrders={() => handleNavigate('orders')}
            onOpenAssistant={() => setAssistantOpen(true)}
            assistantName={config?.tienda?.asistente || config?.asistente?.nombre}
            prefetchActive={prefetchActive}
            mostrarSalud={puedeVerSalud}
            saludCount={saludData?.total_acciones || 0}
            onOpenSalud={() => { setSaludOpen(true); refetchSalud(); }}
          />
          {esSuperadmin ? <SelectorTiendaSoporte /> : null}
        </div>
        {/* P1d: sin large-title — el título de CADA página vive fijo debajo de la barra, en su
            PageHeader/hero propio (patrón Ventas), también en móvil. */}
        <main
          className="page-content"
          ref={pageContentRef}
        >
          {/* Pull-to-Refresh indicator */}
          {(ptrDist > 0 || ptrRefreshing) && (
            <div
              className="ios-ptr"
              style={{ height: ptrRefreshing ? 52 : ptrDist, opacity: ptrRefreshing ? 1 : Math.min(ptrDist / PTR_THRESHOLD, 1) }}
            >
              <div className={`ios-ptr-spinner${ptrRefreshing ? ' ios-ptr-spinner--spinning' : ''}`} />
            </div>
          )}

          <BannerSoloLectura activo={esSoloLectura(config)} desconectada={esDesconectada(config)} suspendida={esSuspendida(config)} finPrueba={avisoFinPrueba(config)} soporte={infoSoporte(config)} />
          <ErrorBoundary key={`${activePage}:${pageResetKey}`}>
          <Suspense fallback={<div className="status-msg">Cargando…</div>}>
          {activePage === 'home' && (
            canAccess('home', userRole) ? (
              /* inicio-centro-control B2: Inicio ya no recibe el catálogo — cada cuadro trae su
                 cifra del servicio dueño. navigateTo (no handleNavigate): el cuadro de cambios
                 de inventario deep-linkea a Usuarios → Auditoría prefiltrada por módulo. */
              <HomePage userRole={userRole} isOwner={isOwner} onNavigate={navigateTo} />
            ) : null
          )}
          {activePage === 'catalog' && (
            canAccess('catalog', userRole) ? (
              <CatalogPage
                products={products}
                loading={loading}
                loadingMore={loadingMore}
                error={error}
                total={total}
                lowStockThreshold={config?.inventario?.umbralStockBajo ?? 5}
                onRefresh={handleRefreshProducts}
                espejoInfo={espejoInfo}
                onReleerVisibles={releerVisiblesEspejo}
                onMigrarEspejo={migrarEspejo}
                userRole={userRole}
                storeName={config?.negocio?.nombre || ''}
                labelConfig={config?.etiquetas}
                catalogoConfig={config?.catalogo}
                plataforma={config?.tienda?.plataforma || 'woo'}
                navTab={navTab}
                soloLectura={esSoloLectura(config)}
                onIrASincronizacion={() => navigateTo('settings', 'sincronizacion')}
              />
            ) : null
          )}
          {activePage === 'inventory' && (
            /* navigateTo (no handleNavigate): Etiqueta/QR de Inventario
               deep-linkea a Catálogo → Etiquetas (F4.4) */
            canAccess('inventory', userRole) ? <InventoryPage config={config} userRole={userRole} isOwner={isOwner} userName={userName} onNavigate={navigateTo} soloLectura={esSoloLectura(config)} /> : null
          )}
          {activePage === 'sales' && (
            canAccess('sales', userRole) ? (
              /* onSaleCompleted es la venta PROPIA del POS: el otro lado de la
                 misma moneda que el refresco por SSE. El vendedor está en
                 Ventas, así que la lista que se recargaba acá (Catálogo, Inicio,
                 búsqueda global) no la mira nadie mientras se vacía. Va
                 silencioso: se funde en vez de reemplazar. */
              <SalesPage
                config={config}
                userRole={userRole}
                onSaleCompleted={() => fetchProducts({ nocache: true, silent: true })}
                prefetchActive={prefetchActive}
                soloLectura={esSoloLectura(config)}
              />
            ) : null
          )}
          {activePage === 'customers' && (
            canAccess('customers', userRole) ? <CustomersPage navTab={navTab} userRole={userRole} soloLectura={esSoloLectura(config)} /> : null
          )}
          {activePage === 'orders' && (
            canAccess('orders', userRole) ? <OrdersPage userRole={userRole} navTab={navTab} soloLectura={esSoloLectura(config)} /> : null
          )}
          {activePage === 'resumen' && (
            canAccess('resumen', userRole) ? <SalesResumenPage onReady={startPrefetch} userRole={userRole} navTab={navTab} onNavigate={navigateTo} /> : null
          )}
          {activePage === 'cashflow' && (
            /* navTab: el wizard de Gastos deep-linkea al modal de Retiro
               (C1 P5 Parte 4) — navigateTo('cashflow', 'retiro-socia') */
            canAccess('cashflow', userRole) ? <CashFlowPage userRole={userRole} isOwner={isOwner} navTab={navTab} /> : null
          )}
          {activePage === 'finanzas' && (
            /* navigateTo (no handleNavigate): el wizard de Gastos necesita mandar
               intención con tab (Caja → modal de retiro, C1 P5 Parte 4) */
            canAccess('finanzas', userRole) ? <FinanzasPage userRole={userRole} onNavigate={navigateTo} navTab={navTab} /> : null
          )}
          {activePage === 'analitica' && (
            /* Doble gate: rol (canAccess) + flag por tienda (config.analitica_activa,
               derivado por el backend en GET /api/config). Sin flag no hay página,
               igual que no hay menú — y el backend responde 404 en /api/analitica. */
            canAccess('analitica', userRole, isOwner) && config?.analitica_activa ? <AnalyticsPage userRole={userRole} isOwner={isOwner} onNavigate={navigateTo} /> : null
          )}
          {activePage === 'agenda' && (
            /* Doble gate: rol (canAccess) + flag por tienda (config.agenda_activa, derivado por el
               backend en GET /api/config). Sin flag no hay página, igual que no hay menú — y el
               backend responde 404 en /api/agenda. */
            canAccess('agenda', userRole, isOwner) && config?.agenda_activa ? <AgendaPage config={config} userRole={userRole} isOwner={isOwner} onNavigate={navigateTo} /> : null
          )}
          {activePage === 'costs' && (
            canAccess('costs', userRole) ? <CostsPage userRole={userRole} isOwner={isOwner} navTab={navTab} /> : null
          )}
          {activePage === 'users' && (
            canAccess('users', userRole, isOwner) ? <UsersPage navTab={navTab} /> : null
          )}
          {activePage === 'uikit' && (
            canAccess('uikit', userRole, isOwner) ? <UiKitPage /> : null
          )}
          {activePage === 'maquetaweb' && (
            canAccess('maquetaweb', userRole, isOwner) ? <MaquetaWebPage /> : null
          )}
          {activePage === 'settings' && (
            canAccess('settings', userRole) ? (
              <SettingsPage config={config} onConfigUpdate={handleConfigUpdate} isOwner={isOwner} navTab={navTab} />
            ) : null
          )}
          </Suspense>
          </ErrorBoundary>
        </main>
        <div className="app-tab-bar">
          <MobileTabBar
            activePage={activePage}
            onNavigate={handleNavigate}
            userRole={userRole}
            isOwner={isOwner}
            analiticaOn={!!config?.analitica_activa}
            agendaOn={!!config?.agenda_activa}
            badges={{ orders: newOrdersCount }}
            onLogout={handleLogout}
            scrollRef={pageContentRef}
          />
        </div>
      </div>

      {orderToast && (
        <OrderNotificationToast
          orders={orderToast}
          onDismiss={() => setOrderToast(null)}
          onNavigateOrders={() => handleNavigate('orders')}
          // pos-boton-wa: arma el wa.me en el backend (registra el envío) y lo abre.
          onSendWa={(orderId) => {
            fetch(`${API_URL}/api/orders/${orderId}/recibo-whatsapp`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
              body: JSON.stringify({ origen: 'pos' }),
            }).then(async (r) => {
              const d = await r.json().catch(() => ({}));
              if (r.ok && d.wa_url) window.open(d.wa_url, '_blank', 'noopener');
            }).catch(() => {});
          }}
        />
      )}

      <SaludPanelDrawer
        open={saludOpen}
        data={saludData}
        onClose={() => setSaludOpen(false)}
        onNavigate={(page, intent) => {
          setSaludOpen(false);
          if (intent?.recordId) navigateTo(page, intent.tab || null, intent.recordId, intent.variationId);
          else handleNavigate(page);
        }}
      />

      <GlobalSearch
        open={globalSearchOpen}
        onClose={() => setGlobalSearchOpen(false)}
        products={products}
        catalogPartial={loading || loadingMore || (total > 0 && products.length < total)}
        onNavigate={(page, intent) => {
          // Deep-link (F3): con recordId la página destino abre el registro.
          // Módulos aún no migrados (catalog/customers) navegan como siempre.
          if (intent?.recordId) navigateTo(page, intent.tab || null, intent.recordId);
          else handleNavigate(page);
        }}
      />

      {assistantMounted && (
        <Suspense fallback={null}>
          <AssistantChat
            userRole={userRole}
            assistantName={config?.tienda?.asistente || config?.asistente?.nombre}
            businessName={config?.negocio?.nombre}
            onNavigate={navigateTo}
            open={assistantOpen}
            onClose={() => setAssistantOpen(false)}
          />
        </Suspense>
      )}
    </div>
    </ToastProvider>
  );
}

export default App;
